"use client";

import { Alert, Button } from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import {
  ResourceFeedback,
  type ChartState,
} from "@softmax/web-ux/resource-feedback";
import * as stylex from "@stylexjs/stylex";
import { coworldLayout } from "./coworld-layout.styles";
import { CoworldStatus } from "./coworld-status";
import type { CreatorStatus } from "./coworld-records";

export function CoworldChecks({
  state,
  onRefresh,
  retry,
  onRetry,
}: Readonly<{
  state: ChartState<
    Readonly<{
      label: string;
      detail: string | null;
      steps: readonly Readonly<{
        id: string;
        status: CreatorStatus;
        feedback: string | null;
      }>[];
      canRetry: boolean;
    }>
  >;
  onRefresh(): void;
  retry: Readonly<{ pending: boolean; error: string | null }>;
  onRetry(): void;
}>) {
  return (
    <div {...stylex.props(coworldLayout.stack)}>
      <ResourceFeedback
        state={state}
        subject="upload checks"
        onRefresh={onRefresh}
      />
      {state.status === "ready" && (
        <>
          <div {...stylex.props(coworldLayout.row)}>
            <h2 {...stylex.props(coworldLayout.heading)}>{state.label}</h2>
            {state.canRetry && (
              <Button size="sm" disabled={retry.pending} onClick={onRetry}>
                {retry.pending ? "Requesting…" : "Rerun checks"}
              </Button>
            )}
          </div>
          {state.detail && <Alert tone="danger">{state.detail}</Alert>}
          <div>
            {state.steps.map((step) => (
              <details key={step.id} {...stylex.props(styles.step)}>
                <summary {...stylex.props(coworldLayout.row)}>
                  <span {...stylex.props(coworldLayout.mono)}>{step.id}</span>
                  <CoworldStatus {...step.status} />
                </summary>
                {step.feedback && (
                  <pre {...stylex.props(styles.transcript)}>
                    {step.feedback}
                  </pre>
                )}
              </details>
            ))}
          </div>
        </>
      )}
      {retry.error && <Alert tone="danger">{retry.error}</Alert>}
    </div>
  );
}
const styles = stylex.create({
  transcript: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    margin: 0,
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
    maxHeight: 320,
    overflowY: "auto",
    padding: spacing.space12,
    backgroundColor: colors.surfaceAlt,
  },
  step: {
    paddingBlock: spacing.space12,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: colors.hairline,
  },
});
